import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import useTextareaHighlight from "./useTextareaHighlight";
import { resizeTextareaPreservingScroll } from "../utils/textareaAutoSize";

const useDemandStudioSearchableTextarea = ({
  value,
  onChange,
  minHeight = "5rem",
  searchTerm = "",
  activeMatchStartIndex = -1,
}) => {
  const textareaRef = useRef(null);
  const highlightRef = useRef(null);
  const activeMarkRef = useRef(null);

  const { segments } = useTextareaHighlight({
    text: value,
    searchTerm,
    activeMatchStartIndex,
  });

  const syncHighlightScroll = useCallback(() => {
    const textarea = textareaRef.current;
    const highlight = highlightRef.current;
    if (!textarea || !highlight) return;
    highlight.scrollTop = textarea.scrollTop;
    highlight.scrollLeft = textarea.scrollLeft;
  }, []);

  const resizeTextarea = useCallback(() => {
    resizeTextareaPreservingScroll({ textarea: textareaRef.current, minHeight });
  }, [minHeight]);

  useLayoutEffect(() => {
    resizeTextarea();
  }, [resizeTextarea, value]);

  const handleChange = useCallback(
    (event) => {
      onChange?.(event);
      requestAnimationFrame(() => {
        resizeTextarea();
        syncHighlightScroll();
      });
    },
    [onChange, resizeTextarea, syncHighlightScroll]
  );

  useEffect(() => {
    if (activeMatchStartIndex < 0) return;
    requestAnimationFrame(() => {
      const activeMark = activeMarkRef.current;
      if (!activeMark) return;
      activeMark.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    });
  }, [activeMatchStartIndex, value, searchTerm]);

  return {
    segments,
    textareaRef,
    highlightRef,
    activeMarkRef,
    handleChange,
    syncHighlightScroll,
  };
};

export default useDemandStudioSearchableTextarea;
